import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Button, Form } from "react-bootstrap";
import { TitlelessCard, HeadingBar, Icon, IconDefinitions, SpinnerDefault, SectionHeader } from "../../components";
import CTEyeService from "../../services/CTEyeService";
import { UrlService } from "../../services";
import { ActiveConfiguration, HeartbeatPayloadDetailed } from "../../data/entities";
import { showErrorMessage, showSuccessMessage } from "../../app/tools";
import { formatDateTime } from "../../app/dateHelpers";
import { CTEyeCommandName, getCtEyeCommand, getKnownInstalledCommands } from "./helper/cteye-commands";

export function CTEyeClientQueuedCommands() {
  const urlParams = useParams();
  const clientId = UrlService.getParamValue("id", urlParams) ?? "";
  const service = useMemo(() => new CTEyeService(), []);

  const [allowedApiCommands, setAllowedApiCommands] = useState<string[]>([]);
  const [selectedCommand, setSelectedCommand] = useState<CTEyeCommandName | "">("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [lastHeartbeat, setLastHeartbeat] = useState<string | null>(null);
  const [activeConfiguration, setActiveConfiguration] = useState<ActiveConfiguration | null>(null);
  const [retentionDays, setRetentionDays] = useState("30");
  const [heartbeatInterval, setHeartbeatInterval] = useState("30");
  const [groupSize, setGroupSize] = useState("10");
  const [rollingWindowSize, setRollingWindowSize] = useState("200");

  const availableCommands = getKnownInstalledCommands(allowedApiCommands);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [commandsResponse, latestHeartbeat] = await Promise.all([
          service.getAllowedCommands(clientId),
          service.getLatestHeartbeat(clientId),
        ]);
        if (commandsResponse) {
          setAllowedApiCommands(commandsResponse.installedCommands || []);
          setLastHeartbeat(commandsResponse.lastHeartbeatDateTime || null);
        }
        if (latestHeartbeat) {
          const payload =
            latestHeartbeat.payload as unknown as HeartbeatPayloadDetailed;
          const config = payload?.activeConfiguration ?? null;
          setActiveConfiguration(config);
          if (config) {
            setHeartbeatInterval(String(config.heartbeatIntervalSeconds));
            setGroupSize(String(config.targetImportRows));
            if (config.rollingAverageWindowSize != null) {
              setRollingWindowSize(String(config.rollingAverageWindowSize));
            }
          }
        }
      } catch (error) {
        console.error("Error loading allowed commands:", error);
        showErrorMessage("Failed to load available commands");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [clientId]);

  const resetParameterDefaults = () => {
    setRetentionDays("30");
    setHeartbeatInterval(
      String(activeConfiguration?.heartbeatIntervalSeconds ?? 30),
    );
    setGroupSize(String(activeConfiguration?.targetImportRows ?? 10));
    setRollingWindowSize(
      String(activeConfiguration?.rollingAverageWindowSize ?? 200),
    );
  };

  const handleCommandChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCommand(e.target.value as CTEyeCommandName | "");
    resetParameterDefaults();
  };

  const handleSelectCommand = (name: CTEyeCommandName) => {
    setSelectedCommand(name);
    resetParameterDefaults();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const buildCommandParameters = (): any => {
    switch (selectedCommand) {
      case "PurgeHeartbeats":
        return { RetentionDays: parseInt(retentionDays) || 0 };
      case "SetHeartbeatInterval":
        return {
          Configurations: [
            { Key: "HeartbeatIntervalSeconds", Value: heartbeatInterval },
          ],
        };
      case "SetTargetImportRows":
        return {
          Configurations: [{ Key: "TargetImportRows", Value: groupSize }],
        };
      case "SetRollingWindowSize":
        return {
          Configurations: [
            { Key: "RollingAverageWindowSize", Value: rollingWindowSize },
          ],
        };
      default:
        return {};
    }
  };

  const validate = (): boolean => {
    if (!selectedCommand) {
      showErrorMessage("Please select a command");
      return false;
    }
    if (
      selectedCommand === "PurgeHeartbeats" &&
      (parseInt(retentionDays) || 0) < 0
    ) {
      showErrorMessage("Retention days must be non-negative");
      return false;
    }
    if (
      selectedCommand === "SetHeartbeatInterval" &&
      (parseInt(heartbeatInterval) || 0) < 1
    ) {
      showErrorMessage("Heartbeat interval must be at least 1 second");
      return false;
    }
    if (
      selectedCommand === "SetTargetImportRows" &&
      (parseInt(groupSize) || 0) < 1
    ) {
      showErrorMessage("Target import rows must be at least 1");
      return false;
    }
    if (
      selectedCommand === "SetRollingWindowSize" &&
      (parseInt(rollingWindowSize) || 0) < 2
    ) {
      showErrorMessage("Rolling window size must be at least 2");
      return false;
    }
    return true;
  };

  const handleQueueCommand = async () => {
    if (!selectedCommand || !validate()) return;
    const command = getCtEyeCommand(selectedCommand);
    setSubmitting(true);
    try {
      const success = await service.queueCommand({
        ctEyeClientId: clientId,
        commandName: command.apiCommandName,
        commandParameters: buildCommandParameters(),
      });
      if (success) {
        showSuccessMessage(
          `"${command.displayName}" queued successfully. It will be sent on the next heartbeat.`,
        );
        setSelectedCommand("");
        resetParameterDefaults();
      } else {
        showErrorMessage("Failed to queue command");
      }
    } catch (error) {
      console.error("Error queuing command:", error);
      showErrorMessage("An error occurred while queuing the command");
    } finally {
      setSubmitting(false);
    }
  };

  const renderCommandForm = () => {
    switch (selectedCommand) {
      case "PurgeHeartbeats":
        return (
          <PurgeHeartbeatsForm
            value={retentionDays}
            onChange={setRetentionDays}
            disabled={submitting}
          />
        );
      case "SetHeartbeatInterval":
        return (
          <HeartbeatIntervalForm
            value={heartbeatInterval}
            currentValue={activeConfiguration?.heartbeatIntervalSeconds}
            onChange={setHeartbeatInterval}
            disabled={submitting}
          />
        );
      case "SetTargetImportRows":
        return (
          <TargetImportRowsForm
            value={groupSize}
            currentValue={activeConfiguration?.targetImportRows}
            onChange={setGroupSize}
            disabled={submitting}
          />
        );
      case "SetRollingWindowSize":
        return (
          <RollingWindowSizeForm
            value={rollingWindowSize}
            currentValue={activeConfiguration?.rollingAverageWindowSize}
            onChange={setRollingWindowSize}
            disabled={submitting}
          />
        );
      default:
        return null;
    }
  };

  if (loading)
    return (
      <div className="text-center py-5">
        <SpinnerDefault />
      </div>
    );

  return (
    <div className="row justify-content-center">
      <div className="col-12 col-lg-11 col-xl-10">
        <div className="mb-3">
          <HeadingBar title="Queue Commands" h={4} />
          <p className="text-muted mb-0">
            Send commands to the CTEye client. Commands are delivered on the
            next heartbeat (within 5 seconds).
          </p>
          {lastHeartbeat && (
            <small className="text-muted">
              <Icon iconName={IconDefinitions.circleCheck} className="me-1" />
              Last heartbeat: {formatDateTime(new Date(lastHeartbeat))}
            </small>
          )}
        </div>

        <div className="row">
          <div className="col-lg-6 mb-3">
            <TitlelessCard>
              <SectionHeader title="Queue New Command" />
              <div className="px-3 pt-3">
                {availableCommands.length === 0 ? (
                  <div className="alert alert-warning">
                    <Icon
                      iconName={IconDefinitions.triangleExclamation}
                      className="me-2"
                    />
                    <strong>No commands available.</strong> The client hasn't
                    sent a heartbeat yet or has no commands installed.
                  </div>
                ) : (
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>Command</Form.Label>
                      <Form.Select
                        value={selectedCommand}
                        onChange={handleCommandChange}
                        disabled={submitting}
                      >
                        <option value="">-- Select a command --</option>
                        {availableCommands.map((cmd) => (
                          <option key={cmd.name} value={cmd.name}>
                            {cmd.displayName}
                          </option>
                        ))}
                      </Form.Select>
                      {selectedCommand && (
                        <Form.Text className="text-muted">
                          {getCtEyeCommand(selectedCommand).description}
                        </Form.Text>
                      )}
                    </Form.Group>

                    {renderCommandForm()}

                    <div className="d-grid gap-2">
                      <Button
                        variant="primary"
                        onClick={handleQueueCommand}
                        disabled={!selectedCommand || submitting}
                      >
                        {submitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Queueing...
                          </>
                        ) : (
                          <>
                            <Icon
                              iconName={IconDefinitions.plus}
                              className="me-2"
                            />
                            Queue Command
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                )}
              </div>
            </TitlelessCard>
          </div>

          <div className="col-lg-6">
            <TitlelessCard>
              <SectionHeader
                title={`Available Commands (${availableCommands.length})`}
              />
              <div className="px-3 pt-3">
                {availableCommands.length === 0 ? (
                  <p className="text-muted">No commands available</p>
                ) : (
                  <div className="list-group list-group-flush">
                    {availableCommands.map((cmd) => (
                      <div key={cmd.name} className="list-group-item px-0 py-2">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <Icon
                                iconName={cmd.icon}
                                className="text-muted"
                              />
                              <span className="fw-semibold">
                                {cmd.displayName}
                              </span>
                            </div>
                            <div className="small text-muted">
                              {cmd.description}
                            </div>
                          </div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleSelectCommand(cmd.name)}
                            disabled={submitting}
                            className="ms-2 flex-shrink-0"
                          >
                            Select
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TitlelessCard>
          </div>
        </div>
      </div>
    </div>
  );
}

function NumericInput({ value, onChange, disabled, isInvalid, append }: {
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  isInvalid: boolean;
  append?: string;
}) {
  const input = (
    <Form.Control
      type="text"
      inputMode="numeric"
      value={value}
      onChange={(e) => {
        const v = e.target.value;
        if (v === "" || /^\d+$/.test(v)) onChange(v);
      }}
      disabled={disabled}
      isInvalid={isInvalid}
    />
  );

  if (append) {
    return (
      <div className="input-group">
        {input}
        <span className="input-group-text">{append}</span>
      </div>
    );
  }
  return input;
}

function PurgeHeartbeatsForm({ value, onChange, disabled }: { value: string; onChange: (v: string) => void; disabled: boolean }) {
  const parsed = parseInt(value);
  const isInvalid = value !== "" && (isNaN(parsed) || parsed < 1);
  
  return (
    <Form.Group className="mb-3">
      <Form.Label>Retention Days</Form.Label>
      <NumericInput value={value} onChange={onChange} disabled={disabled} isInvalid={isInvalid} />
      <Form.Control.Feedback type="invalid">Must be 1 or greater</Form.Control.Feedback>
      <Form.Text className="text-muted">Heartbeats older than this many days will be permanently deleted from the database</Form.Text>
    </Form.Group>
  );
}

function HeartbeatIntervalForm({ value, currentValue, onChange, disabled }: { value: string; currentValue?: number; onChange: (v: string) => void; disabled: boolean }) {
  const parsed = parseInt(value);
  const isInvalid = value !== "" && (isNaN(parsed) || parsed < 1);

  return (
    <Form.Group className="mb-3">
      <Form.Label>Heartbeat Interval</Form.Label>
      <NumericInput value={value} onChange={onChange} disabled={disabled} isInvalid={isInvalid} append="seconds" />
      <Form.Control.Feedback type="invalid">Must be at least 1 second</Form.Control.Feedback>
      {currentValue && <Form.Text className="text-muted">Currently set to: <strong>{currentValue}s</strong></Form.Text>}
      <Form.Text className="text-muted d-block">How often the agent checks in. Lower values mean faster command delivery but more network traffic.</Form.Text>
    </Form.Group>
  );
}

function TargetImportRowsForm({ value, currentValue, onChange, disabled }: { value: string; currentValue?: number; onChange: (v: string) => void; disabled: boolean }) {
  const parsed = parseInt(value);
  const isInvalid = value !== "" && (isNaN(parsed) || parsed < 1);

  return (
    <Form.Group className="mb-3">
      <Form.Label>Target Import Rows</Form.Label>
      <NumericInput value={value} onChange={onChange} disabled={disabled} isInvalid={isInvalid} />
      <Form.Control.Feedback type="invalid">Must be at least 1</Form.Control.Feedback>
      {currentValue && <Form.Text className="text-muted">Currently set to: <strong>{currentValue}</strong></Form.Text>}
      <Form.Text className="text-muted d-block">Target number of rows per import. The group size is dynamically calculated based on this value and the source data size.</Form.Text>
    </Form.Group>
  );
}

function RollingWindowSizeForm({ value, currentValue, onChange, disabled }: { value: string; currentValue?: number; onChange: (v: string) => void; disabled: boolean }) {
  const parsed = parseInt(value);
  const isInvalid = value !== "" && (isNaN(parsed) || parsed < 2);

  return (
    <Form.Group className="mb-3">
      <Form.Label>Rolling Average Window Size</Form.Label>
      <NumericInput value={value} onChange={onChange} disabled={disabled} isInvalid={isInvalid} append="data points" />
      <Form.Control.Feedback type="invalid">Must be at least 2</Form.Control.Feedback>
      {currentValue && <Form.Text className="text-muted">Currently set to: <strong>{currentValue}</strong></Form.Text>}
      <Form.Text className="text-muted d-block">How many FLW data points the device averages over for the live rolling average deviation gauge. The gauge re-warms up after a change.</Form.Text>
    </Form.Group>
  );
}

export default CTEyeClientQueuedCommands;
